import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { TemplateRulesService } from '@/services/templateRules';
import { TemplateType, ContentType, WebsiteTemplateRule, WebsiteSectionRule } from '@/types/templateRules';
import { DEFAULT_TEMPLATE_RULES } from '../../../supabase/functions/generate-content-v3/templateDefaults';
import { DEFAULT_SECTION_TYPES as DEFAULT_SECTION_RULES } from '../../../supabase/functions/generate-content-v3/sectionDefaults';
import AppSidebar from '@/components/Sidebar';
import Header from '@/components/Header';
import { TemplateRulesEditor } from '@/components/template-rules/TemplateRulesEditor';
import { useWebsites } from '@/context/WebsitesContext';

export const TemplateRules = () => {
  const navigate = useNavigate();
  const { currentWebsite } = useWebsites();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [templateRules, setTemplateRules] = useState<Partial<Record<`${TemplateType}:${ContentType}`, WebsiteTemplateRule>>>({});
  const [sectionRules, setSectionRules] = useState<Partial<Record<`${TemplateType}:${ContentType}`, WebsiteSectionRule[]>>>({});

  useEffect(() => {
    const loadTemplateRules = async () => {
      if (!currentWebsite) return;
      
      try {
        setLoading(true);
        const templateTypes: TemplateType[] = ['guide', 'comparison', 'faq'];
        const contentTypes: ContentType[] = ['post', 'page'];

        const rules = await Promise.all(
          templateTypes.flatMap(templateType =>
            contentTypes.map(contentType =>
              TemplateRulesService.getTemplateRules(currentWebsite.id, templateType, contentType)
            )
          )
        );

        const newTemplateRules: Partial<Record<`${TemplateType}:${ContentType}`, WebsiteTemplateRule>> = {};
        const newSectionRules: Partial<Record<`${TemplateType}:${ContentType}`, WebsiteSectionRule[]>> = {};

        rules.forEach((rule, index) => {
          const templateType = templateTypes[Math.floor(index / 2)];
          const contentType = contentTypes[index % 2];
          const key = `${templateType}:${contentType}` as const;
          
          newTemplateRules[key] = rule.templateRule;
          newSectionRules[key] = rule.sectionRules;
        });

        setTemplateRules(newTemplateRules);
        setSectionRules(newSectionRules);
      } catch (error) {
        console.error('Error loading template rules:', error);
        toast.error('Failed to load template rules');
      } finally {
        setLoading(false);
      }
    };

    loadTemplateRules();
  }, [currentWebsite]);

  const handleSave = async () => {
    if (!currentWebsite) return;

    try {
      setSaving(true);
      const templateTypes: TemplateType[] = ['guide', 'comparison', 'faq'];
      const contentTypes: ContentType[] = ['post', 'page'];

      await Promise.all(
        templateTypes.flatMap(templateType =>
          contentTypes.map(contentType => {
            const key = `${templateType}:${contentType}` as const;
            return TemplateRulesService.updateTemplateRules(
              currentWebsite.id,
              templateType,
              contentType,
              templateRules[key]!,
              sectionRules[key] || []
            );
          })
        )
      );

      setHasChanges(false);
      toast.success('Template rules saved successfully');
      navigate('/settings');
    } catch (error) {
      console.error('Error saving template rules:', error);
      toast.error('Failed to save template rules');
    } finally {
      setSaving(false);
    }
  };

  if (!currentWebsite) {
    return (
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-auto p-6">
            <div className="max-w-6xl mx-auto">
              <Card>
                <CardContent className="p-6">
                  <p className="text-center text-muted-foreground">Please select a website to configure template rules.</p>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-auto p-6">
            <div className="max-w-6xl mx-auto flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex w-full bg-background">
      <AppSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/settings')}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Settings
              </Button>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>Template Rules</CardTitle>
              </CardHeader>
              <CardContent>
                <TemplateRulesEditor 
                  websiteId={currentWebsite.id}
                  onSave={handleSave}
                />
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TemplateRules; 